// Formatting helpers shared by the public booking pages.

import { format } from 'date-fns';
import type { PublicBookingInfo, PublicBookingSlot } from '../types/publicBooking';

export const formatBookingCurrency = (amount: number): string =>
	'$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** "123 Main St, Brooklyn, NY 11201" — skips whatever the provider left blank. */
export const formatLocationAddress = (info: PublicBookingInfo): string =>
	[info.address, info.city, [info.state, info.zipCode].filter(Boolean).join(' ').trim()]
		.map(part => part?.trim())
		.filter(Boolean)
		.join(', ');

/** Two-letter monogram for a name, e.g. "Sarah Johnson" → "SJ". */
export const getInitials = (name: string): string =>
	name
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(word => word.charAt(0).toUpperCase())
		.join('');

/**
 * Combines the chosen day and slot into the local `yyyy-MM-ddTHH:mm:ss` string
 * the API expects. Deliberately offset-free, matching the portal's own
 * appointment create call: the time is the salon's wall clock, so stamping the
 * customer's UTC offset would shift the booking for anyone browsing from
 * another timezone.
 */
export const buildAppointmentDateTime = (date: Date, slot: PublicBookingSlot): string => {
	const day = format(date, 'yyyy-MM-dd');
	const time = normalizeSlotTime(slot);
	return `${day}T${time}`;
};

/** Accepts "09:30", "09:30:00" or a "9:30 AM" label and returns "HH:mm:ss". */
const normalizeSlotTime = (slot: PublicBookingSlot): string => {
	const raw = (slot.time || slot.label || '').trim();

	const twentyFourHour = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
	if (twentyFourHour) {
		const [, hours, minutes, seconds] = twentyFourHour;
		return `${hours.padStart(2, '0')}:${minutes}:${seconds ?? '00'}`;
	}

	const twelveHour = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
	if (twelveHour) {
		const [, hourText, minutes, meridiem] = twelveHour;
		let hours = parseInt(hourText, 10);
		if (meridiem.toUpperCase() === 'PM' && hours !== 12) hours += 12;
		if (meridiem.toUpperCase() === 'AM' && hours === 12) hours = 0;
		return `${String(hours).padStart(2, '0')}:${minutes}:00`;
	}

	return '00:00:00';
};

/**
 * Deposit shown before the booking exists: $25 under $125, otherwise 20% of the
 * service total rounded to the nearest $5. Once the booking is created the API's
 * own `deposit` is authoritative and replaces this estimate.
 */
export const estimateDeposit = (serviceTotal: number): number => {
	if (serviceTotal < 125) return 25;
	return Math.round((serviceTotal * 0.2) / 5) * 5;
};
